import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Battery, Building, Home, Zap, Globe, Award, Download, ChevronRight } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import { getProductsByBrand } from '../../data/products';

const WHESPage = () => {
  const features = [
    {
      icon: Award,
      title: "Tier 1 Supplier",
      description: "Recognised as a leading battery energy storage solutions supplier for residential, commercial, and utility applications."
    },
    {
      icon: Globe,
      title: "50+ Countries",
      description: "Global presence with operations spanning over 50 countries and regions worldwide."
    },
    {
      icon: Battery,
      title: "CATL Battery Cells",
      description: "Premium battery modules provided by CATL, one of the world's leading battery manufacturers."
    },
    {
      icon: Zap,
      title: "Smart EMS",
      description: "Intelligent PANGU OS energy management system ensures optimal performance and safety."
    }
  ];

  const solutions = [
    {
      icon: Home,
      name: "Residential ESS",
      description: "Enable clean energy use at home, ensure a stable power supply, and help reduce electricity costs by leveraging peak and off-peak price differences.",
      features: ["Hybrid All-In-One systems", "Hybrid Inverters", "Battery Storage", "AC Chargers"]
    },
    {
      icon: Building,
      name: "Commercial & Industrial ESS",
      description: "Advanced energy storage systems built to meet the evolving needs of C&I applications with all-in-one and modular solutions.",
      features: ["All-In-One solutions", "Modular systems", "Power Conversion Systems", "Control Cabinets"]
    },
    {
      icon: Zap,
      name: "Utility ESS",
      description: "Solutions tailored to strengthen the stability and performance of power systems at grid scale.",
      features: ["Large-scale BESS", "Grid stabilization", "Peak shaving", "Frequency regulation"]
    }
  ];

  const stats = [
    { value: "50+", label: "Countries/Regions Covered" },
    { value: "600+", label: "Global On-site Projects" },
    { value: "20+", label: "Global Service Centers" }
  ];

  const whyWHES = [
    "Comprehensive solutions from design to deployment",
    "Premium CATL battery cells for reliability",
    "Smart PANGU OS energy management",
    "Exceptional safety and durability standards",
    "All-in-one and modular configurations",
    "Local support through global service centers"
  ];

  const whesProducts = getProductsByBrand('whes');

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <Link to="/brands" className="inline-flex items-center gap-2 text-[#3D5A47] hover:text-[#B5D43B] mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Trusted Brands</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Battery className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#B5D43B]">Battery Storage</span>
                </div>
                <h1 className="heading-1 mb-4">
                  <span className="text-[#B5D43B]">WHES</span>
                </h1>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">
                  Tier 1 Battery Energy Storage Solutions
                </p>
                <p className="body-large text-gray-600 mb-8">
                  WHES is one of the leading battery energy storage solutions suppliers, offering comprehensive Battery Energy Storage System Solutions from design to deployment for utilities, C&I, and residential applications.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary gap-2">
                      Get a WHES Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="https://www.whes.com/" target="_blank" rel="noopener noreferrer">
                    <Button className="btn-secondary gap-2">
                      Visit WHES Website
                      <Globe className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/vziwce9r_logo-WHESS.png"
                    alt="WHES Logo"
                    className="w-64 h-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://ui-avatars.com/api/?name=WHES&background=B5D43B&color=3D5A47&size=200";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-[#3D5A47]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index}>
                  <p className="text-4xl font-bold text-[#B5D43B] mb-2">{stat.value}</p>
                  <p className="text-white/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Why Choose WHES?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-4">Energy Storage Solutions</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              WHES provides tailored solutions for every scale of energy storage needs.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <solution.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-bold text-[#3D5A47] mb-2">{solution.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{solution.description}</p>
                  <ul className="space-y-2">
                    {solution.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About WHES */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-2 mb-6">About WHES</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  WHES (Jiangsu Weiheng Intelligent Technology Co., Ltd.) is a Tier 1 battery energy storage solutions supplier with a comprehensive approach to energy storage system solutions.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Their systems are carefully designed with battery modules provided by CATL and smart PANGU OS EMS, ensuring safety, durability, and optimal performance for all applications.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  From residential homes to large-scale utility projects, WHES delivers reliable energy storage solutions that help reduce electricity costs and ensure stable power supply.
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-[#3D5A47] mb-4">Key Highlights</h3>
                {whyWHES.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Available Residential Models */}
        {whesProducts.length > 0 && (
          <section className="py-16 bg-white" data-testid="whes-products-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                    Power Pod Agave Series
                  </span>
                  <h2 className="heading-2 text-[#3D5A47]">Available Residential Models</h2>
                  <p className="text-gray-600 mt-2 max-w-2xl">
                    Two all-in-one variants, one BXC4992 battery module. Pick single-phase or three-phase based on your home's mains supply.
                  </p>
                </div>
                <Link to="/products/batteries" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                  All Batteries <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {whesProducts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/batteries/${p.slug}`}
                    className="group relative bg-white rounded-2xl border border-gray-200 hover:border-[#B5D43B]/60 hover:shadow-xl transition-all overflow-hidden flex"
                    data-testid={`whes-model-${p.slug}`}
                  >
                    <div className={`w-40 sm:w-48 flex-shrink-0 relative flex items-center justify-center p-3 ${p.imageBlendMode === 'normal' ? 'bg-gradient-to-br from-[#f6f8f3] via-white to-[#e9efe2]' : 'bg-gradient-to-br from-[#1a2820] via-[#3D5A47] to-[#2a4032]'}`}>
                      <div className="absolute top-3 left-3 z-10 bg-[#B5D43B] text-[#1a2820] rounded-lg px-2 py-1.5 text-center shadow-md">
                        <span className="block text-[7px] uppercase tracking-widest font-bold leading-none">From</span>
                        <div className="flex items-baseline justify-center leading-none mt-0.5">
                          <span className="text-xl font-black">{p.headlineSpec.value}</span>
                          <span className="text-xs font-black">{p.headlineSpec.unit}</span>
                        </div>
                      </div>
                      {p.image && (
                        <img src={p.image} alt={p.name} className="h-36 w-auto object-contain" style={p.imageBlendMode === 'normal' ? {} : { mixBlendMode: 'multiply' }} loading="lazy" />
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-[#3D5A47] group-hover:text-[#B5D43B] transition-colors leading-tight">
                        {p.series}
                      </h3>
                      <p className="text-xs text-gray-400 mb-2 font-mono">{p.model}</p>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
                        {p.tagline}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3D5A47] bg-[#B5D43B]/15 px-2 py-1 rounded-full">
                          {p.heroPills?.[0]?.label || 'View'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] group-hover:text-[#B5D43B]">
                          View Specs <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Document downloads */}
              <div className="mt-10 bg-gradient-to-br from-[#f8faf5] to-white border border-gray-100 rounded-2xl p-6">
                <h3 className="font-bold text-[#3D5A47] mb-1">Official WHES Documents</h3>
                <p className="text-sm text-gray-600 mb-4">Hosted by Supa Energy Hub for your convenience.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <a
                    href="/brochures/whes/whes-agave-sh-datasheet.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-[#B5D43B]/60 hover:shadow-md transition-all"
                    data-testid="whes-doc-sh"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                      <Download className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#3D5A47]">AGAVE-SH Datasheet</p>
                      <p className="text-xs text-gray-500">Single-phase · PDF</p>
                    </div>
                  </a>
                  <a
                    href="/brochures/whes/whes-agave-th-datasheet.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-[#B5D43B]/60 hover:shadow-md transition-all"
                    data-testid="whes-doc-th"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                      <Download className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#3D5A47]">AGAVE-TH Datasheet</p>
                      <p className="text-xs text-gray-500">Three-phase · PDF</p>
                    </div>
                  </a>
                  <a
                    href="/brochures/whes/whes-residential-warranty-au.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-[#B5D43B]/60 hover:shadow-md transition-all"
                    data-testid="whes-doc-warranty"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                      <Download className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#3D5A47]">Residential Warranty</p>
                      <p className="text-xs text-gray-500">10-year · AU · PDF</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready for WHES Energy Storage?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Let us help you design the perfect energy storage solution using WHES's Tier 1 battery systems.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WHESPage;
